// Total que paga el cliente en cada forma de pago según qué promos admite cada una.
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useCatalogLineInfo } from '@/domains/sales/wizard/api/useCatalogLineInfo';
import { anchorLineOf } from '@/domains/sales/wizard/buildSale';
import type { ReservedLine, WizardSession } from '@/domains/sales/wizard/types';
import { catalogDisplayName } from '@/shared/lib/format/itemDisplayName';
import {
    cachedEvaluation,
    cachedStorePromotions,
    useStoreSalePromotions,
} from '../api/useWizardPromotions';
import type {
    EvaluateSalePromotionsResponse,
    StoreSalePromotionItem,
} from '../api/wizardPromotionsContract';
import {
    heldCodeIsSureKeepOnly,
    heldCodePromotionOf,
    releasedPromotionsOf,
    salePromotionsOf,
    trackTotalCents,
    type PaymentTrack,
    type PromotionFacts,
} from '../lib/paymentOptionPromotions';
import { isSureKeepOnly } from '../lib/promotionAppliesTo';
import { rememberedPromotionName, rememberedSureKeepOnly } from '../lib/promotionNames';
import { releasedFor, type SureKeepReleased } from '../lib/sureKeepRelease';
import { sessionPromotionState, useSessionPromotionState } from '../store/promotionSelectionStore';
import { afterSaleText } from '../components/AfterSaleBenefits';

/** Nombre, alcance y lo que entrega de cada promo, con lo que ya está en caché. */
export function usePromotionFacts(sessionId: string): (promotionId: string) => PromotionFacts {
    const queryClient = useQueryClient();
    const store = useStoreSalePromotions(sessionId).data?.items ?? [];
    return promotionFactsOf(sessionId, store, cachedEvaluation(sessionId, queryClient));
}

/** Lo mismo, fuera de React: con las promos de la tienda y la evaluación que haya. */
export function promotionFactsOf(
    sessionId: string,
    store: readonly StoreSalePromotionItem[],
    evaluation: EvaluateSalePromotionsResponse | undefined,
): (promotionId: string) => PromotionFacts {
    return (promotionId) => {
        const listed = store.find((p) => p.promotionId === promotionId);
        const evaluated = evaluation?.promotions.find((p) => p.promotionId === promotionId);
        const known = listed ?? evaluated;
        const afterSale = (evaluated?.afterSale ?? [])
            .map(afterSaleText)
            .filter(Boolean)
            .join(' · ');
        return {
            name:
                listed?.name ??
                evaluated?.name ??
                rememberedPromotionName(sessionId, promotionId) ??
                'Promo de venta',
            sureKeepOnly: known
                ? isSureKeepOnly(known)
                : rememberedSureKeepOnly(sessionId, promotionId),
            text: afterSale !== '' ? afterSale : (listed?.customerText ?? ''),
            ...(known?.paymentOptions !== undefined && { paymentOptions: known.paymentOptions }),
            categories: listed?.scope.productCategories ?? null,
        };
    };
}

/** Lo que baja el código según la evaluación en caché, si aplica. */
export function evaluatedCodeCents(
    queryClient: QueryClient,
    sessionId: string,
    code: string,
): number | undefined {
    const evaluated = cachedEvaluation(sessionId, queryClient)?.discountCode;
    return evaluated?.applicable && evaluated.codeText === code
        ? evaluated.totalDiscountCents
        : undefined;
}

/** Formas de pago del código según la evaluación en caché; undefined si no está evaluado. */
export function evaluatedCodePaymentOptions(
    queryClient: QueryClient,
    sessionId: string,
    code: string,
): readonly string[] | null | undefined {
    const evaluated = cachedEvaluation(sessionId, queryClient)?.discountCode;
    return evaluated?.codeText === code ? evaluated.paymentOptions : undefined;
}

/** Total de la venta en una forma de pago: sin las promos que esa forma no admite. */
export function usePaymentTrackTotal(
    sessionId: string,
    session: WizardSession,
    track: PaymentTrack,
    saleTotalCents: number,
): number {
    const queryClient = useQueryClient();
    const facts = usePromotionFacts(sessionId);
    const { descriptions } = useCatalogLineInfo(sessionId);
    const { sureKeepReleased, anyFinancierCodes = [] } = useSessionPromotionState(sessionId);
    return trackTotalOf({
        session,
        track,
        saleTotalCents,
        facts,
        released: sureKeepReleased,
        anyFinancierCodes,
        productName: (line) => catalogDisplayName({ sku: line.sku, ...line.product }, descriptions),
        evaluatedCents: (c) => evaluatedCodeCents(queryClient, sessionId, c),
        codePaymentOptions: (c) => evaluatedCodePaymentOptions(queryClient, sessionId, c),
    });
}

/** Total de la forma de pago con lo que hay en caché; para quien no puede usar el hook. */
export function cachedTrackTotalCents(
    sessionId: string,
    session: WizardSession,
    track: PaymentTrack,
    saleTotalCents: number,
    queryClient: QueryClient,
): number {
    const state = sessionPromotionState(sessionId);
    return trackTotalOf({
        session,
        track,
        saleTotalCents,
        facts: promotionFactsOf(
            sessionId,
            cachedStorePromotions(sessionId, queryClient)?.items ?? [],
            cachedEvaluation(sessionId, queryClient),
        ),
        released: state.sureKeepReleased,
        anyFinancierCodes: state.anyFinancierCodes ?? [],
        productName: (line) => line.sku,
        evaluatedCents: (c) => evaluatedCodeCents(queryClient, sessionId, c),
        codePaymentOptions: (c) => evaluatedCodePaymentOptions(queryClient, sessionId, c),
    });
}

function trackTotalOf({
    session,
    track,
    saleTotalCents,
    facts,
    released: stored,
    anyFinancierCodes,
    productName,
    evaluatedCents,
    codePaymentOptions,
}: {
    session: WizardSession;
    track: PaymentTrack;
    saleTotalCents: number;
    facts: (promotionId: string) => PromotionFacts;
    released: SureKeepReleased | undefined;
    anyFinancierCodes: readonly string[];
    productName: (line: ReservedLine) => string;
    evaluatedCents: (code: string) => number | undefined;
    codePaymentOptions: (code: string) => readonly string[] | null | undefined;
}): number {
    const released = releasedFor(stored, anchorLineOf(session)?.lineId);
    const code = heldCodePromotionOf(session, {
        sureKeepOnly: (c) =>
            heldCodeIsSureKeepOnly(session, c, anyFinancierCodes, codePaymentOptions(c)),
        productName,
        evaluatedCents,
    });
    const held = [...salePromotionsOf(session, { facts, productName }), ...(code ? [code] : [])];
    const promos = [
        ...held,
        ...releasedPromotionsOf(session, released, held, { facts, productName }),
    ];
    return trackTotalCents(
        saleTotalCents,
        promos,
        track,
        (lineId) => session.reservedLines?.find((l) => l.lineId === lineId)?.discountCents,
    );
}
